import React, { useState } from 'react';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';
import {
    HandThumbUpIcon as ThumbUpSolid,
    HandThumbDownIcon as ThumbDownSolid,
} from '@heroicons/react/24/solid';
import { conciergeFeedback } from '../../services/api';
import { track, EVENTS } from '../../services/analytics';

// Per-REPLY feedback (founder 2026-08-06: "capture user's feedback with a
// thumbs up or down"). The practice this follows:
//   · rate the ANSWER, not the session — only per-reply feedback tells you
//     which answer landed badly;
//   · one tap is the whole requirement — the reason picker is optional and
//     never blocks;
//   · reasons are a closed set of codes, so nothing new stores student prose;
//   · optimistic and fire-and-forget — feedback must never interrupt the
//     conversation, and a failed write is silent;
//   · the choice persists visibly, and can be changed.
const REASONS = [
    ['inaccurate', 'Not accurate'],
    ['unhelpful', "Didn't help"],
    ['confusing', 'Confusing'],
    ['too_generic', 'Too generic'],
    ['wrong_tone', 'Wrong tone'],
];

// A short, non-reversible digest of the reply text — a STABLE identity for
// the answer being rated. turn_index drifts (confirm summaries and upload
// bubbles never enter the stored history; the thread clips at 60 turns), so
// an index-keyed rating can end up pointing at a different reply (review
// blocker 2026-08-06). This stores no content: only the key travels.
function replyKey(text) {
    let h = 5381;
    for (let i = 0; i < (text || '').length; i += 1) {
        h = ((h << 5) + h + text.charCodeAt(i)) | 0;
    }
    return `k${(h >>> 0).toString(36)}${((text || '').length % 997).toString(36)}`;
}

// A2 (founder pick, 2026-08-09). The original control got ZERO taps in 481
// turns — 14px icons at 2.1:1 contrast (the control floor is 3:1) with no
// words and a ~22px target on a mostly-mobile product. Visibility was only
// half the bug: an unlabeled icon never ASKS anything. So the latest
// substantive reply carries a worded question from Astra ("Did this help?")
// with real buttons, and older replies keep compact — but honest-contrast,
// 32px — icons so the transcript stays rateable without the prompt nagging
// down the page. One visible ask at a time, ever.
export default function ReplyFeedback({ email, threadId, turnIndex, replyText, prominent = false }) {
    const [rating, setRating] = useState(null);
    const [askReason, setAskReason] = useState(false);
    const [thanks, setThanks] = useState(false);

    const send = (value, reason) => {
        setRating(value);
        // Mirrored to PostHog (audit H3): backend telemetry counts ratings
        // but can't join them to session replay or funnels — you couldn't
        // watch the recording of a thumbs-down.
        track(EVENTS.CONCIERGE_FEEDBACK_RATED, { verdict: value, reason: reason || 'none' });
        conciergeFeedback(email, {
            threadId, turnIndex, rating: value, reason,
            replyKey: replyKey(replyText),
        });
        if (value === 'down' && !reason) setAskReason(true);
        else { setAskReason(false); setThanks(true); }
    };

    if (!threadId) return null;
    return (
        <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid="reply-feedback">
            {prominent && !rating && (
                /* Words are what make it a question from Astra instead of
                   furniture. Rendered only until a rating exists — after the
                   tap it collapses to the compact confirmation state. */
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E0DED8] bg-[#F8F6F0] py-1 pl-3 pr-1.5"
                      data-testid="reply-feedback-prompt">
                    <span className="text-[12px] text-[#6B6B6B]">Did this help?</span>
                    <button type="button" aria-label="This reply was helpful"
                            onClick={() => send('up')}
                            data-testid="reply-thumb-up"
                            className="inline-flex h-8 items-center gap-1 rounded-full border border-[#E0DED8] bg-white px-2.5 text-[12.5px] font-semibold text-[#1A4D2E] hover:border-[#1A4D2E] transition-colors">
                        <HandThumbUpIcon className="h-4 w-4" /> Yes
                    </button>
                    <button type="button" aria-label="This reply was not helpful"
                            onClick={() => send('down')}
                            data-testid="reply-thumb-down"
                            className="inline-flex h-8 items-center gap-1 rounded-full border border-[#E0DED8] bg-white px-2.5 text-[12.5px] font-semibold text-[#B7791F] hover:border-[#B7791F] transition-colors">
                        <HandThumbDownIcon className="h-4 w-4" /> Not really
                    </button>
                </span>
            )}
            {(!prominent || rating) && (<>
            <button type="button" aria-label="This reply was helpful"
                    aria-pressed={rating === 'up'} onClick={() => send('up')}
                    data-testid={prominent ? 'reply-thumb-up-rated' : 'reply-thumb-up'}
                    className={`grid h-8 w-8 place-items-center rounded-lg border transition-colors ${rating === 'up'
                        ? 'border-[#1A4D2E] bg-[#F0F7F1] text-[#1A4D2E]'
                        : 'border-[#E0DED8] bg-white text-[#6B6B6B] hover:border-[#1A4D2E] hover:text-[#1A4D2E]'}`}>
                {rating === 'up' ? <ThumbUpSolid className="h-4 w-4" />
                                 : <HandThumbUpIcon className="h-4 w-4" />}
            </button>
            <button type="button" aria-label="This reply was not helpful"
                    aria-pressed={rating === 'down'} onClick={() => send('down')}
                    data-testid={prominent ? 'reply-thumb-down-rated' : 'reply-thumb-down'}
                    className={`grid h-8 w-8 place-items-center rounded-lg border transition-colors ${rating === 'down'
                        ? 'border-[#B7791F] bg-[#FDF6E9] text-[#B7791F]'
                        : 'border-[#E0DED8] bg-white text-[#6B6B6B] hover:border-[#B7791F] hover:text-[#B7791F]'}`}>
                {rating === 'down' ? <ThumbDownSolid className="h-4 w-4" />
                                   : <HandThumbDownIcon className="h-4 w-4" />}
            </button>
            </>)}
            {askReason && (
                <span className="flex flex-wrap items-center gap-1" data-testid="reply-feedback-reasons">
                    <span className="text-[10px] text-[#9A968C]">What was off?</span>
                    {REASONS.map(([code, label]) => (
                        <button key={code} type="button" onClick={() => send('down', code)}
                                data-testid={`reply-reason-${code}`}
                                className="rounded-full border border-[#E0DED8] px-1.5 py-0.5 text-[10px]
                                    text-[#6B6B6B] hover:border-[#B7791F] hover:text-[#B7791F]">
                            {label}
                        </button>
                    ))}
                    <button type="button" onClick={() => { setAskReason(false); setThanks(true); }}
                            className="text-[10px] text-[#9A968C] hover:text-[#6B6B6B]">skip</button>
                </span>
            )}
            {thanks && !askReason && (
                <span className="text-[10px] text-[#9A968C]">Thanks — noted.</span>
            )}
        </div>
    );
}
